/**
 * Consumer-local regular-column tables over TableModel (ADR 0020, AC-SOURCE-002, CN-IMPL-004).
 *
 * Plugs into `TableColumnProvider`: each stream is one TableModel whose query is replaced on every
 * request. The query re-posts the table definition under the stream's stable `userViewKey`, the
 * watchlist pattern, so the server amends the view in place and answers with the same table key.
 * TableModel's same-key path then keeps the TableClient and its subscription. A different key
 * makes TableModel build a new TableClient and emit `NEW_TABLE_RESET`. TableModel also keeps
 * owning cookies, proxy targets, end-of-data recovery, and reconnection.
 *
 * Values are current only while the subscription that produced them stays open (OD-RETAIN-001):
 * on `NEW_TABLE_RESET` every key the previous TableClient reported is removed before the new one
 * reports anything. A socket disconnect keeps the TableClient and emits no reset marker, so the
 * change to `disconnected` removes them too; the subscription reports them again after
 * reconnecting (OD-PROVIDER-001). Rows are borrowed table-client rows read synchronously; only their keys are
 * kept, so diff deletions can be reported by key.
 */
import { effect, signal, untracked } from '@preact/signals-react';
import { RowField, RowIdField } from '@thinkalpha/table-client';
import type { RowUpdateBatch } from '@thinkalpha/table-client';
import type {
    KeyedRowBackend,
    KeyedRowListener,
    KeyedRowStream,
    KeyedTableRequest,
} from '@thinkalpha/ui-keyed-columns/tableColumnProvider';
import { KEYED_TABLE_WINDOW_SIZE } from '@thinkalpha/ui-table/adapters/tableClientRowBackend';
import type { TableCreationQuery, TableModel } from 'src/models/TableModel';
import { NEW_TABLE_RESET } from 'src/models/TableModel';
import { v4 } from 'uuid';

/** The TableModel surface a stream reads; structural so tests can fake it. */
export type KeyedTableModel = Pick<TableModel, 'init' | 'updatesWithReset$' | 'connectionStatus'> & Disposable;

export interface TableModelRowBackendOptions {
    createTable(): KeyedTableModel;
    /** The table query serving `request`, posted under the stream's stable `userViewKey`. */
    query(request: KeyedTableRequest<string, string>, userViewKey: string): TableCreationQuery;
    /** Row field carrying the symbol in this table's rows. */
    readonly symbolFieldId: string;
    readonly devtoolsName: string;
}

/** Holds a stream's TableModel disabled until the first request. */
const IDLE_QUERY: TableCreationQuery = {
    queryKey: ['stitched-columns', 'idle'],
    queryFn: () => null,
    enabled: false,
    staleTime: 0,
    gcTime: 0,
};

class TableModelRowStream implements KeyedRowStream<string, string> {
    readonly #listener: KeyedRowListener<string, string, unknown>;
    readonly #options: TableModelRowBackendOptions;
    readonly #query = signal<TableCreationQuery>(IDLE_QUERY);
    readonly #userViewKey = v4();
    /** Row identity → key for the current TableClient, so deletions and resets report by key. */
    readonly #rowKeys = new Map<number, string>();
    /** One reused entry per requested field: a row update allocates nothing here. */
    #entries: [string, unknown][] = [];
    readonly #lifetime = new DisposableStack();

    constructor(options: TableModelRowBackendOptions, listener: KeyedRowListener<string, string, unknown>) {
        this.#options = options;
        this.#listener = listener;
        const table = this.#lifetime.use(options.createTable());
        table.init({
            queryOptions: this.#query,
            // A fixed window: an amended table keeps its subscription and bounds (OD-BOUNDS-001).
            initBounds: { firstRow: 0, windowSize: KEYED_TABLE_WINDOW_SIZE },
            devtoolsName: options.devtoolsName,
        });
        const rows = table.updatesWithReset$.subscribe((batch) => {
            if (batch === NEW_TABLE_RESET) {
                this.#releaseReported();
            } else {
                this.#receive(batch);
            }
        });
        this.#lifetime.defer(() => rows.unsubscribe());
        // Listener calls run untracked: the provider and its grid must not subscribe this effect.
        this.#lifetime.defer(
            effect(() => {
                if (table.connectionStatus.value === 'disconnected') {
                    untracked(() => this.#releaseReported());
                }
            }),
        );
    }

    request(request: KeyedTableRequest<string, string>): void {
        if (this.#lifetime.disposed) {
            return;
        }
        this.#entries = request.fields.map((field) => [field, undefined]);
        this.#query.value = this.#options.query(request, this.#userViewKey);
    }

    [Symbol.dispose](): void {
        this.#lifetime.dispose();
    }

    /** The previous TableClient's values stop being live: remove every key it reported. */
    #releaseReported(): void {
        if (this.#rowKeys.size === 0) {
            return;
        }
        this.#listener.begin();
        for (const key of new Set(this.#rowKeys.values())) {
            this.#listener.removed(key);
        }
        this.#listener.end();
        this.#rowKeys.clear();
    }

    #receive(batch: RowUpdateBatch): void {
        this.#listener.begin();
        if (batch.type === 'diff') {
            for (const rowId of batch.deletedRowIds) {
                const key = this.#rowKeys.get(rowId);
                this.#rowKeys.delete(rowId);
                if (key !== undefined) {
                    this.#listener.removed(key);
                }
            }
        }
        const symbolFieldId = this.#options.symbolFieldId;
        const entries = this.#entries;
        for (const row of batch) {
            const key = row[symbolFieldId];
            if (typeof key !== 'string') {
                continue;
            }
            this.#rowKeys.set(row[RowIdField] ?? row[RowField], key);
            for (const entry of entries) {
                entry[1] = row[entry[0]];
            }
            this.#listener.row(key, entries);
        }
        this.#listener.end();
    }
}

export class TableModelRowBackend implements KeyedRowBackend<string, string, unknown> {
    readonly #options: TableModelRowBackendOptions;

    constructor(options: TableModelRowBackendOptions) {
        this.#options = options;
    }

    open(listener: KeyedRowListener<string, string, unknown>): KeyedRowStream<string, string> {
        return new TableModelRowStream(this.#options, listener);
    }
}
